import React, { useEffect, useState } from 'react';
import { Col, Container, Form, Row } from 'react-bootstrap';
import { useNotification } from '../../hooks/NotificationContext';
import AutomationDetailsForm from './AutomationDetailsForm';
import Loader from '../elements/Loader';
import {
  addGithubActionsDetails,
  addJenkinsDetails,
} from '../../modules/automation';
import { useParams } from 'react-router-dom';
import { getCurrentProject } from '../../modules/project';

const AutomationIntegration = ({ isAdmin }) => {
  const [error, setError] = useState(null);
  const [selectedAutomation, setSelectedAutomation] = useState('');
  const { addNotification } = useNotification();
  const [showAuthToken, setShowAuthToken] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { projectId } = useParams();

  const defaultJenkinsData = {
    jenkinsurl: '',
    jenkinsUsername: '',
    jenkinsAuthToken: '',
  };

  const defaultGithubData = {
    githubRepo: '',
    githubUsername: '',
    githubAuthToken: '',
  };

  const [jenkinsData, setJenkinsData] = useState(defaultJenkinsData);
  const [githubData, setGithubData] = useState(defaultGithubData);

  const fetchProjectData = async () => {
    setLoading(true);
    try {
      const response = await getCurrentProject(projectId);

      setSelectedAutomation(response?.automation_method || 'jenkins');
      setJenkinsData({
        jenkinsurl: response.jenkinsurl || '',
        jenkinsUsername: response.jenkins_username || '',
        jenkinsAuthToken: response.jenkins_auth_token || '',
      });

      setGithubData({
        githubRepo: response.github_repo || '',
        githubUsername: response.github_username || '',
        githubAuthToken: response.github_auth_token || '',
      });
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    if (selectedAutomation === 'jenkins') {
      setJenkinsData((prevData) => ({
        ...prevData,
        [id]: value,
      }));
    } else {
      setGithubData((prevData) => ({
        ...prevData,
        [id]: value,
      }));
    }
  };

  const handleSaveClick = async () => {
    const data = selectedAutomation === 'jenkins' ? jenkinsData : githubData;
    if (
      (selectedAutomation === 'jenkins' &&
        data.jenkinsurl &&
        data.jenkinsUsername &&
        data.jenkinsAuthToken) ||
      (selectedAutomation === 'github' &&
        data.githubRepo &&
        data.githubUsername &&
        data.githubAuthToken)
    ) {
      setSubmitting(true);
      try {
        selectedAutomation === 'jenkins'
          ? await addJenkinsDetails(
              { ...data, automationMethod: selectedAutomation },
              projectId
            )
          : await addGithubActionsDetails(
              { ...data, automationMethod: selectedAutomation },
              projectId
            );

        addNotification(
          'success',
          `${
            selectedAutomation === 'jenkins' ? 'Jenkins' : 'GitHub actions'
          } details saved successfully`
        );
        fetchProjectData();
      } catch (error) {
        addNotification(
          'danger',
          error.message || `Failed to save ${selectedAutomation} details`
        );
      }
      setSubmitting(false);
    } else {
      setError('Please fill all the required fields');
    }
  };

  useEffect(() => {
    fetchProjectData();
  }, []);

  return loading ? (
    <Loader />
  ) : (
    <Container fluid className="px-0">
      <h5>Automate your test execution with Jenkins or GitHub Actions</h5>
      <Container fluid className="py-3 px-0">
        <Row>
          <Col md={3}>
            <Form.Label className="required">Select Execution Tool</Form.Label>
            <Form.Select
              aria-label="Select Execution Tool"
              value={selectedAutomation}
              onChange={(e) => setSelectedAutomation(e.target.value)}
              disabled={!isAdmin}>
              <option value="jenkins">Jenkins</option>
              <option value="github">GitHub Actions</option>
            </Form.Select>
          </Col>
        </Row>
        <Row className="mt-4">
          <AutomationDetailsForm
            data={selectedAutomation === 'jenkins' ? jenkinsData : githubData}
            handleChange={handleChange}
            handleSave={handleSaveClick}
            showAuthToken={showAuthToken}
            setShowAuthToken={setShowAuthToken}
            submitting={submitting}
            error={error}
            readOnly={!isAdmin}
            labels={
              selectedAutomation === 'jenkins'
                ? {
                    urlLabel: 'Jenkins URL',
                    usernameLabel: 'Jenkins Username',
                    tokenLabel: 'Jenkins Auth Token',
                    urlPlaceholder: 'jenkinsurl',
                    usernamePlaceholder: 'jenkinsUsername',
                    tokenPlaceholder: 'jenkinsAuthToken',
                  }
                : {
                    urlLabel: 'GitHub Repo Name',
                    usernameLabel: 'GitHub Username',
                    tokenLabel: 'GitHub Auth Token',
                    urlPlaceholder: 'githubRepo',
                    usernamePlaceholder: 'githubUsername',
                    tokenPlaceholder: 'githubAuthToken',
                  }
            }
          />
        </Row>
      </Container>
    </Container>
  );
};

export default AutomationIntegration;
